/**
 * ShiftPlanner.js — Weekly visual shift scheduler
 * Roles that can access: owner, admin, manager
 *
 * Firestore collection: scheduledShifts
 * Doc shape:
 *   userId, userName, userRole, userEmail
 *   date (YYYY-MM-DD), weekStart (YYYY-MM-DD of Sunday)
 *   startTime (HH:MM), endTime (HH:MM)
 *   position (string), notes (string)
 *   status: 'scheduled' | 'cancelled'
 *   createdBy, createdAt, updatedAt
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  collection, query, where, getDocs, addDoc, updateDoc, deleteDoc, doc, Timestamp,
} from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../context/AuthContext';
import './Shifts.css';

// ─── constants ───────────────────────────────────────────────────────────────
const PLANNER_ROLES = ['owner', 'admin', 'manager'];
const STAFF_ROLES   = ['employee', 'manager', 'admin', 'owner'];

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const DAYS_FULL = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const POSITIONS = [
  { label: 'Cook',     color: '#f59e0b', bg: 'rgba(245,158,11,0.18)' },
  { label: 'Cashier',  color: '#22c55e', bg: 'rgba(34,197,94,0.16)' },
  { label: 'Server',   color: '#3b82f6', bg: 'rgba(59,130,246,0.16)' },
  { label: 'Prep',     color: '#a78bfa', bg: 'rgba(167,139,250,0.16)' },
  { label: 'Delivery', color: '#f472b6', bg: 'rgba(244,114,182,0.16)' },
  { label: 'Manager',  color: '#06b6d4', bg: 'rgba(6,182,212,0.16)' },
  { label: 'Cleaning', color: '#94a3b8', bg: 'rgba(148,163,184,0.16)' },
  { label: 'Other',    color: '#e2e8f0', bg: 'rgba(226,232,240,0.12)' },
];

const SHIFT_TEMPLATES = [
  { label: 'Opening',    startTime: '11:00', endTime: '14:00' },
  { label: 'Morning',   startTime: '09:00', endTime: '15:00' },
  { label: 'Mid',       startTime: '10:00', endTime: '15:00' },
  { label: 'Closing',   startTime: '16:30', endTime: '22:00' },
  { label: 'Evening',   startTime: '18:00', endTime: '22:00' },
  { label: 'Evening Mid', startTime: '19:00', endTime: '23:00' },
];

// ─── helpers ─────────────────────────────────────────────────────────────────
function getWeekSunday(d) {
  const dt = new Date(d);
  dt.setDate(dt.getDate() - dt.getDay());
  return toISO(dt);
}

function toISO(d) {
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
}

function addDays(isoDate, n) {
  const d = new Date(isoDate + 'T00:00:00');
  d.setDate(d.getDate() + n);
  return toISO(d);
}

function fmt12(hhmm) {
  if (!hhmm) return '';
  const [h, m] = hhmm.split(':').map(Number);
  const ampm = h >= 12 ? 'pm' : 'am';
  const h12  = h % 12 || 12;
  return `${h12}:${String(m).padStart(2,'0')}${ampm}`;
}

function shiftHours(start, end) {
  if (!start || !end) return 0;
  const [sh, sm] = start.split(':').map(Number);
  const [eh, em] = end.split(':').map(Number);
  const mins = (eh * 60 + em) - (sh * 60 + sm);
  return Math.max(0, mins / 60);
}

function positionMeta(label) {
  return POSITIONS.find(p => p.label === label) || POSITIONS[POSITIONS.length - 1];
}

function initials(name) {
  if (!name) return '?';
  return name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
}

function fmtDisplayDate(iso) {
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function isToday(iso) {
  return iso === toISO(new Date());
}

// ─── component ───────────────────────────────────────────────────────────────
const ShiftPlanner = () => {
  const { user, role, loading } = useAuth();
  const navigate = useNavigate();

  const canEdit = PLANNER_ROLES.includes(role);

  // week state — weekStart is always Sunday
  const [weekStart, setWeekStart] = useState(() => getWeekSunday(new Date()));

  // data
  const [staff, setStaff]     = useState([]);   // all eligible users
  const [planned, setPlanned] = useState([]);   // scheduledShifts docs for this week
  const [actual, setActual]   = useState({});   // clockedIn map: userId+date → 'clocked-in' | 'completed'
  const [loadingData, setLoadingData] = useState(true);

  // modal state
  const [modal, setModal] = useState(null); // { mode: 'add'|'edit', userId, userName, date, doc? }
  const [mStart,    setMStart]    = useState('09:00');
  const [mEnd,      setMEnd]      = useState('17:00');
  const [mPosition, setMPosition] = useState('Cook');
  const [mNotes,    setMNotes]    = useState('');
  const [mSaving,   setMSaving]   = useState(false);
  const [mTemplate, setMTemplate] = useState('');

  // filters
  const [filterRole, setFilterRole] = useState('all');
  const [copyBusy, setCopyBusy]     = useState(false);
  const [toast, setToast]           = useState('');

  // derived week days
  const weekDays = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)), [weekStart]);

  // ── auth guard ──
  useEffect(() => {
    if (loading) return;
    if (!user || !PLANNER_ROLES.includes(role)) navigate('/');
  }, [user, role, loading, navigate]);

  // ── load staff ──
  const loadStaff = useCallback(async () => {
    try {
      const snap = await getDocs(collection(db, 'users'));
      const list = [];
      snap.forEach(d => {
        const data = { uid: d.id, ...d.data() };
        if (STAFF_ROLES.includes(data.role)) list.push(data);
      });
      list.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      setStaff(list);
    } catch (err) { console.error('load staff', err); }
  }, []);

  // ── load planned shifts for this week ──
  const loadPlanned = useCallback(async () => {
    setLoadingData(true);
    try {
      // eslint-disable-next-line no-unused-vars
      const weekEnd = addDays(weekStart, 6);
      const q = query(
        collection(db, 'scheduledShifts'),
        where('weekStart', '==', weekStart),
      );
      const snap = await getDocs(q);
      const list = [];
      snap.forEach(d => list.push({ id: d.id, ...d.data() }));
      setPlanned(list);
    } catch (err) { console.error('load planned', err); }
    setLoadingData(false);
  }, [weekStart]);

  // ── load actual clock-in status for the week ──
  const loadActual = useCallback(async () => {
    try {
      const start = new Date(weekStart + 'T00:00:00');
      const end   = new Date(addDays(weekStart, 6) + 'T23:59:59');
      const q = query(
        collection(db, 'shifts'),
        where('clockIn', '>=', Timestamp.fromDate(start)),
        where('clockIn', '<=', Timestamp.fromDate(end)),
      );
      const snap = await getDocs(q);
      const map = {};
      snap.forEach(d => {
        const s = { id: d.id, ...d.data() };
        const date = s.clockIn?.toDate ? toISO(s.clockIn.toDate()) : '';
        if (date) map[`${s.userId}|${date}`] = s.status;
      });
      setActual(map);
    } catch (err) { console.error('load actual', err); }
  }, [weekStart]);

  useEffect(() => { loadStaff(); }, [loadStaff]);
  useEffect(() => { loadPlanned(); loadActual(); }, [loadPlanned, loadActual]);

  // ── week navigation ──
  const prevWeek = () => setWeekStart(w => addDays(w, -7));
  const nextWeek = () => setWeekStart(w => addDays(w, 7));
  const thisWeek = () => setWeekStart(getWeekSunday(new Date()));

  // ── week label ──
  const weekLabel = useMemo(() => {
    const s = fmtDisplayDate(weekStart);
    const e = fmtDisplayDate(addDays(weekStart, 6));
    return `${s} — ${e}`;
  }, [weekStart]);

  // ── lookup planned by userId + date ──
  const planMap = useMemo(() => {
    const m = {};
    planned.forEach(p => {
      const key = `${p.userId}|${p.date}`;
      if (!m[key]) m[key] = [];
      m[key].push(p);
    });
    return m;
  }, [planned]);

  // ── hours per employee this week ──
  const empHours = useMemo(() => {
    const h = {};
    planned.forEach(p => {
      if (p.status === 'cancelled') return;
      h[p.userId] = (h[p.userId] || 0) + shiftHours(p.startTime, p.endTime);
    });
    return h;
  }, [planned]);

  // ── daily total hours ──
  const dayHours = useMemo(() => {
    const h = {};
    planned.forEach(p => {
      if (p.status === 'cancelled') return;
      h[p.date] = (h[p.date] || 0) + shiftHours(p.startTime, p.endTime);
    });
    return h;
  }, [planned]);

  // ── filtered staff ──
  const visibleStaff = useMemo(() => {
    if (filterRole === 'all') return staff;
    if (filterRole === 'manager') return staff.filter(s => s.role === 'manager' || s.role === 'admin' || s.role === 'owner');
    return staff.filter(s => s.role === 'employee');
  }, [staff, filterRole]);

  // ── open modal to add shift ──
  const openAdd = (userId, userName, date) => {
    if (!canEdit) return;
    setMStart('09:00'); setMEnd('17:00');
    setMPosition('Cook'); setMNotes(''); setMTemplate('');
    setModal({ mode: 'add', userId, userName, date });
  };

  // ── open modal to edit shift ──
  const openEdit = (e, planDoc) => {
    e.stopPropagation();
    if (!canEdit) return;
    setMStart(planDoc.startTime); setMEnd(planDoc.endTime);
    setMPosition(planDoc.position || 'Cook'); setMNotes(planDoc.notes || ''); setMTemplate('');
    setModal({ mode: 'edit', userId: planDoc.userId, userName: planDoc.userName, date: planDoc.date, doc: planDoc });
  };

  // ── apply template ──
  const applyTemplate = (label) => {
    const t = SHIFT_TEMPLATES.find(t => t.label === label);
    if (t) { setMStart(t.startTime); setMEnd(t.endTime); }
    setMTemplate(label);
  };

  // ── save modal ──
  const saveModal = async () => {
    if (!mStart || !mEnd || mSaving) return;
    if (shiftHours(mStart, mEnd) <= 0) { showToast('End time must be after start time'); return; }
    setMSaving(true);
    try {
      const payload = {
        userId: modal.userId,
        userName: modal.userName,
        userRole: staff.find(s => s.uid === modal.userId)?.role || '',
        userEmail: staff.find(s => s.uid === modal.userId)?.email || '',
        date: modal.date,
        weekStart,
        startTime: mStart,
        endTime: mEnd,
        position: mPosition,
        notes: mNotes.trim(),
        status: 'scheduled',
        updatedAt: Timestamp.now(),
      };
      if (modal.mode === 'add') {
        payload.createdBy = user.uid;
        payload.createdAt = Timestamp.now();
        await addDoc(collection(db, 'scheduledShifts'), payload);
        showToast('Shift added ✓');
      } else {
        await updateDoc(doc(db, 'scheduledShifts', modal.doc.id), payload);
        showToast('Shift updated ✓');
      }
      setModal(null);
      loadPlanned();
    } catch (err) {
      console.error('save shift', err);
      showToast('Error saving shift');
    }
    setMSaving(false);
  };

  // ── delete shift ──
  const deleteShift = async (e, planDoc) => {
    e.stopPropagation();
    if (!canEdit || !window.confirm(`Remove this shift for ${planDoc.userName}?`)) return;
    try {
      await deleteDoc(doc(db, 'scheduledShifts', planDoc.id));
      showToast('Shift removed');
      loadPlanned();
    } catch (err) {
      console.error('delete shift', err);
      showToast('Error removing shift');
    }
  };

  // ── copy last week ──
  const copyLastWeek = async () => {
    if (!canEdit || copyBusy) return;
    const prevStart = addDays(weekStart, -7);
    setCopyBusy(true);
    try {
      // fetch last week's shifts
      const q = query(collection(db, 'scheduledShifts'), where('weekStart', '==', prevStart));
      const snap = await getDocs(q);
      if (snap.empty) { showToast('No shifts found in previous week'); setCopyBusy(false); return; }
      // get current week's existing dates to avoid duplicates
      const existingKeys = new Set(planned.map(p => `${p.userId}|${p.date}`));
      const toCreate = [];
      snap.forEach(d => {
        const p = d.data();
        if (p.status === 'cancelled') return;
        // map prev date → this week date (same day index)
        const prevDate = new Date(p.date + 'T00:00:00');
        const dayIdx   = prevDate.getDay(); // 0-6
        const newDate  = addDays(weekStart, dayIdx);
        const key = `${p.userId}|${newDate}`;
        if (existingKeys.has(key)) return; // skip if already scheduled
        toCreate.push({ ...p, date: newDate, weekStart, createdBy: user.uid, createdAt: Timestamp.now(), updatedAt: Timestamp.now() });
      });
      await Promise.all(toCreate.map(s => addDoc(collection(db, 'scheduledShifts'), s)));
      showToast(`Copied ${toCreate.length} shift(s) from last week ✓`);
      loadPlanned();
    } catch (err) {
      console.error('copy last week', err);
      showToast('Error copying shifts');
    }
    setCopyBusy(false);
  };

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(''), 3000);
  };

  // ── modal hours preview ──
  const modalHrs = shiftHours(mStart, mEnd);

  if (loading || !user) return null;

  return (
    <div className="shift-page">
      <div className="shift-page__inner sp-wide">

        {/* ── Toast ── */}
        {toast && <div className="sp-toast">{toast}</div>}

        {/* ── Hero Header ── */}
        <div className="sp-hero">
          <div className="sp-hero__left">
            <span className="sp-hero__pill">📅 Shift Planner</span>
            <h1 className="sp-hero__title">Weekly Schedule</h1>
            <p className="sp-hero__sub">Plan and assign shifts for your team each week</p>
          </div>
          <div className="sp-hero__right">
            <div className="sp-hero__week-nav">
              <button className="sp-nav-btn" onClick={prevWeek} title="Previous week">‹</button>
              <div className="sp-hero__week-label">
                <span className="sp-hero__week-range">{weekLabel}</span>
                <button className="sp-this-week-btn" onClick={thisWeek}>This Week</button>
              </div>
              <button className="sp-nav-btn" onClick={nextWeek} title="Next week">›</button>
            </div>
          </div>
        </div>

        {/* ── Toolbar ── */}
        <div className="sp-toolbar">
          <div className="sp-toolbar__filters">
            <span className="sp-toolbar__label">View:</span>
            {['all', 'manager', 'employee'].map(r => (
              <button
                key={r}
                className={`sp-filter-btn${filterRole === r ? ' sp-filter-btn--active' : ''}`}
                onClick={() => setFilterRole(r)}
              >
                {r === 'all' ? '👥 All Staff' : r === 'manager' ? '🎯 Managers' : '👷 Employees'}
              </button>
            ))}
          </div>
          <div className="sp-toolbar__right">
            <button className={`sp-copy-btn${copyBusy ? ' sp-copy-btn--busy' : ''}`} onClick={copyLastWeek} disabled={copyBusy || !canEdit}>
              {copyBusy ? '⏳ Copying…' : '📋 Copy Last Week'}
            </button>
          </div>
        </div>

        {/* ── Weekly Grid ── */}
        {loadingData ? (
          <div className="shift-loading">Loading schedule…</div>
        ) : (
          <div className="sp-grid-wrapper">
            <table className="sp-grid">
              <thead>
                <tr className="sp-grid__head-row">
                  <th className="sp-grid__th sp-grid__th--emp">Employee</th>
                  {weekDays.map((date, i) => (
                    <th key={date} className={`sp-grid__th${isToday(date) ? ' sp-grid__th--today' : ''}`}>
                      <span className="sp-grid__day-name">{DAYS[i]}</span>
                      <span className="sp-grid__day-date">{fmtDisplayDate(date)}</span>
                      {dayHours[date] > 0 && (
                        <span className="sp-grid__day-hours">{dayHours[date].toFixed(1)}h</span>
                      )}
                    </th>
                  ))}
                  <th className="sp-grid__th sp-grid__th--total">Week Hrs</th>
                </tr>
              </thead>
              <tbody>
                {visibleStaff.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="sp-grid__empty">No staff found. Add users first.</td>
                  </tr>
                ) : visibleStaff.map(emp => (
                  <tr key={emp.uid} className="sp-grid__row">
                    {/* Employee info */}
                    <td className="sp-grid__emp-cell">
                      <div className="sp-emp">
                        <div className="sp-emp__avatar" style={emp.role === 'manager' || emp.role === 'admin' || emp.role === 'owner'
                          ? { background: 'linear-gradient(135deg, #06b6d4, #0891b2)' }
                          : { background: 'linear-gradient(135deg, #f59e0b, #d97706)' }
                        }>
                          {initials(emp.name)}
                        </div>
                        <div className="sp-emp__info">
                          <span className="sp-emp__name">{emp.name || emp.email}</span>
                          <span className="sp-emp__role">{emp.role}</span>
                        </div>
                      </div>
                    </td>

                    {/* Day cells */}
                    {weekDays.map(date => {
                      const key       = `${emp.uid}|${date}`;
                      const shifts    = planMap[key] || [];
                      const isClocked = actual[key];
                      const todayCell = isToday(date);

                      return (
                        <td
                          key={date}
                          className={`sp-grid__cell${todayCell ? ' sp-grid__cell--today' : ''}${canEdit ? ' sp-grid__cell--editable' : ''}`}
                          onClick={() => shifts.length === 0 && openAdd(emp.uid, emp.name || emp.email, date)}
                        >
                          {shifts.map(s => {
                            const pos = positionMeta(s.position);
                            return (
                              <div
                                key={s.id}
                                className={`sp-block${s.status === 'cancelled' ? ' sp-block--cancelled' : ''}`}
                                style={{ background: pos.bg, borderLeft: `3px solid ${pos.color}` }}
                                onClick={e => openEdit(e, s)}
                              >
                                <div className="sp-block__top">
                                  <span className="sp-block__time">{fmt12(s.startTime)}–{fmt12(s.endTime)}</span>
                                  {canEdit && (
                                    <button className="sp-block__del" onClick={e => deleteShift(e, s)} title="Remove shift">×</button>
                                  )}
                                </div>
                                <div className="sp-block__hours">{shiftHours(s.startTime, s.endTime).toFixed(1)}h</div>
                                {isClocked && (
                                  <div className={`sp-block__status sp-block__status--${isClocked}`}>
                                    {isClocked === 'active' || isClocked === 'on-break' ? '🟢 Clocked In' : '✓ Done'}
                                  </div>
                                )}
                                {s.notes ? <div className="sp-block__note" title={s.notes}>📝</div> : null}
                              </div>
                            );
                          })}
                          {shifts.length === 0 && canEdit && (
                            <div className="sp-cell__add-hint">+ Add</div>
                          )}
                          {shifts.length > 0 && canEdit && (
                            <div
                              className="sp-cell__add-more"
                              onClick={e => { e.stopPropagation(); openAdd(emp.uid, emp.name || emp.email, date); }}
                            >+ Add</div>
                          )}
                        </td>
                      );
                    })}

                    {/* Weekly hour total */}
                    <td className="sp-grid__total-cell">
                      <span className={`sp-total${(empHours[emp.uid] || 0) >= 40 ? ' sp-total--over' : ''}`}>
                        {(empHours[emp.uid] || 0).toFixed(1)}h
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>

              {/* Footer: daily totals */}
              <tfoot>
                <tr className="sp-grid__foot-row">
                  <td className="sp-grid__foot-label">Daily Total</td>
                  {weekDays.map(date => (
                    <td key={date} className="sp-grid__foot-cell">
                      {(dayHours[date] || 0) > 0
                        ? <span className="sp-foot-val">{(dayHours[date] || 0).toFixed(1)}h</span>
                        : <span className="sp-foot-empty">—</span>
                      }
                    </td>
                  ))}
                  <td className="sp-grid__foot-cell">
                    <span className="sp-foot-val sp-foot-val--total">
                      {Object.values(dayHours).reduce((a, b) => a + b, 0).toFixed(1)}h
                    </span>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}

        {/* ── Stats row ── */}
        {!loadingData && (
          <div className="sp-stats">
            <div className="sp-stat">
              <span className="sp-stat__val">{planned.filter(p => p.status !== 'cancelled').length}</span>
              <span className="sp-stat__lbl">Shifts Scheduled</span>
            </div>
            <div className="sp-stat">
              <span className="sp-stat__val">{visibleStaff.filter(e => (empHours[e.uid] || 0) > 0).length}</span>
              <span className="sp-stat__lbl">Staff Assigned</span>
            </div>
            <div className="sp-stat">
              <span className="sp-stat__val">{Object.values(dayHours).reduce((a, b) => a + b, 0).toFixed(0)}h</span>
              <span className="sp-stat__lbl">Total Hours</span>
            </div>
            <div className="sp-stat">
              <span className="sp-stat__val">{visibleStaff.filter(e => (empHours[e.uid] || 0) >= 40).length}</span>
              <span className="sp-stat__lbl">Full-Time (40h+)</span>
            </div>
          </div>
        )}

      </div>

      {/* ──────────────────── Modal ──────────────────── */}
      {modal && (
        <div className="shift-edit-overlay" onClick={() => !mSaving && setModal(null)}>
          <div className="sp-modal" onClick={e => e.stopPropagation()}>

            <div className="sp-modal__header">
              <div>
                <h2 className="sp-modal__title">{modal.mode === 'add' ? '➕ Add Shift' : '✏️ Edit Shift'}</h2>
                <p className="sp-modal__sub">{modal.userName} · {DAYS_FULL[new Date(modal.date + 'T00:00:00').getDay()]} {fmtDisplayDate(modal.date)}</p>
              </div>
              <button className="sp-modal__close" onClick={() => !mSaving && setModal(null)}>×</button>
            </div>

            {/* Templates */}
            <div className="sp-modal__section-label">Quick Templates</div>
            <div className="sp-modal__templates">
              {SHIFT_TEMPLATES.map(t => (
                <button
                  key={t.label}
                  className={`sp-tpl-btn${mTemplate === t.label ? ' sp-tpl-btn--active' : ''}`}
                  onClick={() => applyTemplate(t.label)}
                >
                  <span className="sp-tpl-btn__name">{t.label}</span>
                  <span className="sp-tpl-btn__time">{fmt12(t.startTime)}–{fmt12(t.endTime)}</span>
                </button>
              ))}
            </div>

            {/* Time */}
            <div className="sp-modal__row">
              <div className="shift-edit-modal__field">
                <label className="shift-edit-modal__label">Start Time</label>
                <input type="time" className="shift-edit-modal__input" value={mStart} onChange={e => setMStart(e.target.value)} />
              </div>
              <div className="shift-edit-modal__field">
                <label className="shift-edit-modal__label">End Time</label>
                <input type="time" className="shift-edit-modal__input" value={mEnd} onChange={e => setMEnd(e.target.value)} />
              </div>
            </div>

            {/* Hours preview */}
            {modalHrs > 0 && (
              <div className="shift-edit-modal__preview">
                ⏱ {modalHrs.toFixed(1)} hours — {fmt12(mStart)} to {fmt12(mEnd)}
              </div>
            )}

            {/* Notes */}
            <div className="shift-edit-modal__field">
              <label className="shift-edit-modal__label">Notes (optional)</label>
              <textarea
                className="shift-edit-modal__input shift-edit-modal__textarea"
                placeholder="e.g. Cover for John, training day…"
                value={mNotes}
                onChange={e => setMNotes(e.target.value)}
                rows={2}
              />
            </div>

            {/* Actions */}
            <div className="shift-edit-modal__actions">
              <button className="shift-edit-modal__btn shift-edit-modal__btn--cancel" onClick={() => !mSaving && setModal(null)} disabled={mSaving}>
                Cancel
              </button>
              <button className="shift-edit-modal__btn shift-edit-modal__btn--save" onClick={saveModal} disabled={mSaving || modalHrs <= 0}>
                {mSaving ? 'Saving…' : modal.mode === 'add' ? 'Add Shift' : 'Save Changes'}
              </button>
            </div>

          </div>
        </div>
      )}
    </div>
  );
};

export default ShiftPlanner;
